import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, Trophy, ImageOff } from 'lucide-react';
import { SPORT_TILES as SPORT_COLUMNS, formatStat } from '@/lib/sportStats';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sport-aware columns for the Season Totals tiles and the Player Season Stats table.

export function TeamStatsDialog({ shareToken, teamId, open, onOpenChange, leaderboardShareToken = '', eventId = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !shareToken || !teamId) return;
    setLoading(true);
    setData(null);
    fetch(`${API_URL}/api/share/${shareToken}/team-stats/${teamId}`)
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [shareToken, teamId, open]);

  const sport = data?.sport || 'basketball';
  const isBasketball = sport === 'basketball';
  const sportCols = SPORT_COLUMNS[sport] || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="public-team-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {data?.team?.logo && (
              <img src={`${API_URL}/api/files/${data.team.logo}`} alt="" className="w-8 h-8 rounded-full object-cover" />
            )}
            <span className="text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
              {data?.team?.name || 'Team Stats'}
            </span>
          </DialogTitle>
          {data && (
            <DialogDescription className="text-xs text-slate-500">
              {data.games_played} games played · {data.wins}-{data.losses}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading && (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#00247D]" />
          </div>
        )}

        {!loading && data && (
          <div className="space-y-5">
            {/* Team photo banner — object-contain on a dark backdrop so the
                full frame is visible regardless of aspect ratio (portrait /
                landscape / group-shot). Previously cropped to h-40 which
                sliced off players' heads on tall photos. */}
            {data.team.team_photo && (
              <div
                className="rounded-lg overflow-hidden bg-slate-900 flex items-center justify-center"
                data-testid="public-team-photo"
              >
                <img
                  src={`${API_URL}/api/files/${data.team.team_photo}`}
                  alt=""
                  className="w-full h-auto max-h-[520px] object-contain"
                />
              </div>
            )}

            {/* Season Totals */}
            <div>
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" /> Season Totals
              </h3>
              {isBasketball ? (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                  {[
                    { label: 'PTS', value: data.team_totals.pts, accent: 'text-[#00247D]' },
                    { label: 'REB', value: data.team_totals.reb },
                    { label: 'AST', value: data.team_totals.ast },
                    { label: 'STL', value: data.team_totals.stl },
                    { label: 'BLK', value: data.team_totals.blk },
                    { label: 'FG%', value: `${data.team_totals.fg_pct ?? 0}%` },
                  ].map(cell => (
                    <div key={cell.label} className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{cell.label}</div>
                      <div className={`text-xl font-black ${cell.accent || 'text-slate-800'}`}>{cell.value}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center"
                  data-testid={`team-totals-${sport}`}
                >
                  {sportCols.map((cell, i) => (
                    <div key={cell.key} className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">{cell.label}</div>
                      <div className={`text-xl font-black ${i === 0 ? 'text-[#00247D]' : 'text-slate-800'}`}>
                        {formatStat(cell.key, data.team_totals?.[cell.key] ?? 0)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Player season stats */}
            <div>
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Player Season Stats</h3>
              {data.players.length === 0 ? (
                <div className="py-8 text-center text-slate-500 text-sm">No player stats yet for this team.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center w-8">#</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-left">Player</th>
                        <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">GP</th>
                        {isBasketball ? (
                          <>
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">PTS</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">PPG</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">RPG</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">APG</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">STL</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">BLK</th>
                            <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">FG%</th>
                          </>
                        ) : (
                          sportCols.map(col => (
                            <th key={col.key} className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase text-center">{col.label}</th>
                          ))
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data.players.map((p, i) => (
                        <tr key={`${p.name}-${i}`} className="border-b border-slate-100" data-testid={`public-team-player-row-${i}`}>
                          <td className="px-2 py-2 font-mono text-center text-slate-500 text-xs">{p.jersey_number || '–'}</td>
                          <td className="px-2 py-2 font-semibold text-slate-800">
                            {leaderboardShareToken ? (
                              <Link
                                to={`/stats/leaderboard/${leaderboardShareToken}/career?${new URLSearchParams(
                                  Object.assign({ team: data.team?.name || '', name: p.name }, eventId ? { event_id: eventId } : {})
                                ).toString()}`}
                                className="inline-flex items-center gap-2 hover:text-amber-600 hover:underline underline-offset-2"
                                onClick={() => onOpenChange?.(false)}
                                data-testid={`public-team-player-link-${i}`}>
                                {p.headshot_path ? (
                                  <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />
                                ) : (
                                  <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center shrink-0">
                                    <ImageOff className="w-3 h-3" />
                                  </span>
                                )}
                                <span>{p.name}</span>
                              </Link>
                            ) : (
                              <span className="inline-flex items-center gap-2">
                                {p.headshot_path ? (
                                  <img src={`${API_URL}/api/files/${p.headshot_path}`} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" onError={e => { e.target.style.display = 'none'; }} />
                                ) : (
                                  <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center shrink-0">
                                    <ImageOff className="w-3 h-3" />
                                  </span>
                                )}
                                <span>{p.name}</span>
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center text-slate-500">{p.gp}</td>
                          {isBasketball ? (
                            <>
                              <td className="px-2 py-2 text-center font-bold text-[#00247D]">{p.pts}</td>
                              <td className="px-2 py-2 text-center">{p.ppg}</td>
                              <td className="px-2 py-2 text-center">{p.rpg}</td>
                              <td className="px-2 py-2 text-center">{p.apg}</td>
                              <td className="px-2 py-2 text-center">{p.stl}</td>
                              <td className="px-2 py-2 text-center">{p.blk}</td>
                              <td className="px-2 py-2 text-center text-slate-500">{p.fg_pct}%</td>
                            </>
                          ) : (
                            sportCols.map((col, ci) => (
                              <td
                                key={col.key}
                                className={`px-2 py-2 text-center ${ci === 0 ? 'font-bold text-[#00247D]' : 'text-slate-700'}`}
                              >
                                {formatStat(col.key, p.sport_stats?.[col.key] ?? 0)}
                              </td>
                            ))
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
